/**
 * Formulario de configuración de OLT/POP
 * Permite configurar múltiples OLTs embebidas, slots, tarjetas PON y puertos
 * con conexión a hilos de cables troncales
 * @module components/forms/OLTConfigForm
 */

import { useState } from 'react'
import { Plus, Trash2, Server, Cpu, Cable, AlertTriangle, ChevronDown, ChevronUp, Circle, Router } from 'lucide-react'
import { cableLabelForSelect, cableLabelShort } from '@/utils/cableDisplayLabel'

// Colores TIA-598
const COLORES_TIA598 = [
  { nombre: 'Azul', codigo: 'azul', hex: '#0066CC' },
  { nombre: 'Naranja', codigo: 'naranja', hex: '#FF6600' },
  { nombre: 'Verde', codigo: 'verde', hex: '#00AA00' },
  { nombre: 'Marrón', codigo: 'marron', hex: '#8B4513' },
  { nombre: 'Gris', codigo: 'gris', hex: '#808080' },
  { nombre: 'Blanco', codigo: 'blanco', hex: '#FFFFFF' },
  { nombre: 'Rojo', codigo: 'rojo', hex: '#FF0000' },
  { nombre: 'Negro', codigo: 'negro', hex: '#000000' },
  { nombre: 'Amarillo', codigo: 'amarillo', hex: '#FFCC00' },
  { nombre: 'Violeta', codigo: 'violeta', hex: '#9900CC' },
  { nombre: 'Rosa', codigo: 'rosa', hex: '#FF66CC' },
  { nombre: 'Turquesa', codigo: 'turquesa', hex: '#00CCCC' },
]

const getColorHex = (color: string) => COLORES_TIA598.find(c => c.codigo === color)?.hex || '#CCCCCC'

// Conexión de puerto PON a cable
interface ConexionPuertoCable {
  cableId: string
  cableNombre?: string
  bufferNum?: number
  bufferColor?: string
  fibraNum: number
  fibraColor: string
  fibraId: string
}

// Puerto PON con conexión a cable
interface OLTPuerto {
  numero: number
  tipo: 'GPON' | 'EPON' | 'XGS-PON'
  estado: 'up' | 'down' | 'disabled'
  potenciaTx?: number
  clientesConectados: number
  descripcion?: string
  conexionCable?: ConexionPuertoCable
}

// Slot de OLT
interface OLTSlot {
  numero: number
  tipo: 'PON' | 'UPLINK' | 'CONTROL' | 'VACIO'
  tarjeta?: string
  puertos: OLTPuerto[]
}

// OLT individual (equipo)
interface OLTEquipo {
  id: string
  marca: string
  modelo: string
  serial?: string
  tecnologia: 'GPON' | 'EPON' | 'XGS-PON'
  gestionIP?: string
  gestionPuerto?: number
  slots: OLTSlot[]
  expanded?: boolean
}

// Configuración completa del POP con OLTs
interface OLTConfig {
  // Datos del POP (si aplica)
  nombreSitio?: string
  direccion?: string
  // OLTs embebidas
  olts: OLTEquipo[]
  // Mantener compatibilidad con config anterior (OLT individual)
  marca?: string
  modelo?: string
  serial?: string
  slots?: OLTSlot[]
  tecnologia?: 'GPON' | 'EPON' | 'XGS-PON'
  capacidadTotal?: number
  gestionIP?: string
  gestionPuerto?: number
}

// Cable con estructura de fibras
interface CableConFibras {
  id: string
  codigo?: string
  nombre?: string
  tipo: string
  numeroFibras: number
  tieneBuffers: boolean
  buffers?: Array<{
    numero: number
    color: string
    fibras: Array<{
      numero: number
      color: string
      estado: string
      fibraId: string
    }>
  }>
  fibras?: Array<{
    numero: number
    color: string
    estado: string
    fibraId: string
  }>
}

interface OLTConfigFormProps {
  config?: OLTConfig
  onChange: (config: OLTConfig) => void
  cablesConectados?: CableConFibras[]
}

const MARCAS_OLT = ['Huawei', 'ZTE', 'Nokia', 'Fiberhome', 'BDCOM', 'VSOL', 'C-Data', 'Otro']
const TECNOLOGIAS = ['GPON', 'EPON', 'XGS-PON'] as const

const isValidIP = (ip: string): boolean => {
  if (!ip) return true
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  return ipRegex.test(ip)
}

const generateId = () => `olt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

export function OLTConfigForm({ config, onChange, cablesConectados = [] }: OLTConfigFormProps) {
  // Migrar config anterior a nuevo formato si es necesario
  const migrateConfig = (cfg?: OLTConfig): OLTConfig => {
    if (!cfg) {
      return { olts: [] }
    }
    
    // Si ya tiene olts[], usar eso
    if (cfg.olts && cfg.olts.length > 0) {
      return cfg
    }
    
    // Si tiene marca/modelo (formato anterior), migrar a una OLT embebida
    if (cfg.marca || cfg.modelo || (cfg.slots && cfg.slots.length > 0)) {
      return {
        nombreSitio: cfg.nombreSitio,
        direccion: cfg.direccion,
        olts: [{
          id: generateId(),
          marca: cfg.marca || 'Huawei',
          modelo: cfg.modelo || '',
          serial: cfg.serial,
          tecnologia: cfg.tecnologia || 'GPON',
          gestionIP: cfg.gestionIP,
          gestionPuerto: cfg.gestionPuerto,
          slots: cfg.slots || [],
          expanded: true
        }]
      }
    }
    
    return { olts: [], nombreSitio: cfg.nombreSitio, direccion: cfg.direccion }
  }

  const [localConfig, setLocalConfig] = useState<OLTConfig>(migrateConfig(config))
  const [editingPuerto, setEditingPuerto] = useState<{ oltId: string; slotIdx: number; puertoIdx: number } | null>(null)

  const updateConfig = (updates: Partial<OLTConfig>) => {
    const newConfig = { ...localConfig, ...updates }
    setLocalConfig(newConfig)
    onChange(newConfig)
  }

  // ======== Funciones para OLTs ========
  const addOLT = () => {
    const nuevaOLT: OLTEquipo = {
      id: generateId(),
      marca: 'Huawei',
      modelo: '',
      tecnologia: 'GPON',
      slots: [],
      expanded: true
    }
    updateConfig({ olts: [...localConfig.olts, nuevaOLT] })
  }

  const removeOLT = (oltId: string) => {
    updateConfig({ olts: localConfig.olts.filter(olt => olt.id !== oltId) })
  }

  const updateOLT = (oltId: string, updates: Partial<OLTEquipo>) => {
    updateConfig({
      olts: localConfig.olts.map(olt => 
        olt.id === oltId ? { ...olt, ...updates } : olt
      )
    })
  }

  const toggleOLTExpanded = (oltId: string) => {
    updateOLT(oltId, { expanded: !localConfig.olts.find(o => o.id === oltId)?.expanded })
  }

  // ======== Funciones para Slots ========
  const addSlot = (oltId: string) => {
    const olt = localConfig.olts.find(o => o.id === oltId)
    if (!olt) return

    const nuevoSlot: OLTSlot = {
      numero: olt.slots.length + 1,
      tipo: 'PON',
      puertos: []
    }
    updateOLT(oltId, { slots: [...olt.slots, nuevoSlot] })
  }

  const removeSlot = (oltId: string, slotIndex: number) => {
    const olt = localConfig.olts.find(o => o.id === oltId)
    if (!olt) return

    const newSlots = olt.slots.filter((_, i) => i !== slotIndex)
    updateOLT(oltId, { slots: newSlots })
  }

  const updateSlot = (oltId: string, slotIndex: number, updates: Partial<OLTSlot>) => {
    const olt = localConfig.olts.find(o => o.id === oltId)
    if (!olt) return

    const newSlots = [...olt.slots]
    newSlots[slotIndex] = { ...newSlots[slotIndex], ...updates }
    updateOLT(oltId, { slots: newSlots })
  }

  // ======== Funciones para Puertos ========
  const addPuertoToSlot = (oltId: string, slotIndex: number) => {
    const olt = localConfig.olts.find(o => o.id === oltId)
    if (!olt) return

    const slot = olt.slots[slotIndex]
    const nuevoPuerto: OLTPuerto = {
      numero: slot.puertos.length + 1,
      tipo: olt.tecnologia,
      estado: 'down',
      clientesConectados: 0
    }
    updateSlot(oltId, slotIndex, { puertos: [...slot.puertos, nuevoPuerto] })
  }

  const removePuertoFromSlot = (oltId: string, slotIndex: number, puertoIndex: number) => {
    const olt = localConfig.olts.find(o => o.id === oltId)
    if (!olt) return

    const slot = olt.slots[slotIndex]
    const newPuertos = slot.puertos.filter((_, i) => i !== puertoIndex)
    updateSlot(oltId, slotIndex, { puertos: newPuertos })
  }

  const updatePuerto = (oltId: string, slotIndex: number, puertoIndex: number, updates: Partial<OLTPuerto>) => {
    const olt = localConfig.olts.find(o => o.id === oltId)
    if (!olt) return

    const slot = olt.slots[slotIndex]
    const newPuertos = [...slot.puertos]
    newPuertos[puertoIndex] = { ...newPuertos[puertoIndex], ...updates }
    updateSlot(oltId, slotIndex, { puertos: newPuertos })
  }

  // ======== Conexión Puerto PON a Cable ========
  const conectarPuertoACable = (
    oltId: string,
    slotIndex: number,
    puertoIndex: number,
    conexion: ConexionPuertoCable | undefined
  ) => {
    updatePuerto(oltId, slotIndex, puertoIndex, { conexionCable: conexion })
    setEditingPuerto(null)
  }

  // Obtener fibras disponibles de un cable
  const getFibrasDisponibles = (cable: CableConFibras) => {
    const fibras: Array<{
      fibraId: string
      bufferNum?: number
      bufferColor?: string
      fibraNum: number
      fibraColor: string
      estado: string
    }> = []

    if (cable.tieneBuffers && cable.buffers) {
      cable.buffers.forEach(buffer => {
        buffer.fibras.forEach(fibra => {
          fibras.push({
            fibraId: fibra.fibraId,
            bufferNum: buffer.numero,
            bufferColor: buffer.color,
            fibraNum: fibra.numero,
            fibraColor: fibra.color,
            estado: fibra.estado,
          })
        })
      })
    } else if (cable.fibras) {
      cable.fibras.forEach(fibra => {
        fibras.push({
          fibraId: fibra.fibraId,
          fibraNum: fibra.numero,
          fibraColor: fibra.color,
          estado: fibra.estado,
        })
      })
    }

    return fibras
  }

  // ======== Validaciones ========
  const getValidationWarnings = () => {
    const warnings: string[] = []
    
    if (localConfig.olts.length === 0) {
      warnings.push('No hay OLTs configuradas en este POP')
    }

    localConfig.olts.forEach((olt, idx) => {
      if (!olt.modelo.trim()) {
        warnings.push(`OLT ${idx + 1}: Se recomienda especificar el modelo`)
      }
      
      if (olt.gestionIP && !isValidIP(olt.gestionIP)) {
        warnings.push(`OLT ${idx + 1}: Formato de IP de gestión inválido`)
      }
      
      const slotsPONSinPuertos = olt.slots.filter(s => s.tipo === 'PON' && s.puertos.length === 0)
      if (slotsPONSinPuertos.length > 0) {
        warnings.push(`OLT ${idx + 1}: ${slotsPONSinPuertos.length} slot(s) PON sin puertos`)
      }

      const puertosSinConexion = olt.slots.reduce((acc, s) =>
        acc + s.puertos.filter(p => p.estado === 'up' && !p.conexionCable).length, 0
      )
      if (puertosSinConexion > 0) {
        warnings.push(`OLT ${idx + 1}: ${puertosSinConexion} puerto(s) UP sin conexión a cable`)
      }
    })
    
    return warnings
  }

  const warnings = getValidationWarnings()

  // Calcular estadísticas
  const totalOLTs = localConfig.olts.length
  const totalPuertos = localConfig.olts.reduce((acc, olt) => 
    acc + olt.slots.reduce((a, s) => a + (s.tipo === 'PON' ? s.puertos.length : 0), 0), 0
  )
  const puertosConectados = localConfig.olts.reduce((acc, olt) =>
    acc + olt.slots.reduce((a, s) => a + s.puertos.filter(p => p.conexionCable).length, 0), 0
  )

  return (
    <div className="space-y-6">
      {/* Advertencias */}
      {warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200 text-sm">Advertencias</p>
              <ul className="mt-1 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                {warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Información del Sitio */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nombre del Sitio</label>
          <input
            type="text"
            value={localConfig.nombreSitio || ''}
            onChange={(e) => updateConfig({ nombreSitio: e.target.value })}
            placeholder="Ej: POP Central"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Dirección</label>
          <input
            type="text"
            value={localConfig.direccion || ''}
            onChange={(e) => updateConfig({ direccion: e.target.value })}
            placeholder="Dirección física"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
      </div>

      {/* Lista de OLTs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Router className="w-4 h-4" />
            OLTs del Sitio ({totalOLTs})
          </h4>
          <button
            type="button"
            onClick={addOLT}
            className="text-sm px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Agregar OLT
          </button>
        </div>

        {localConfig.olts.length === 0 ? (
          <div className="text-center py-6 text-gray-500 border-2 border-dashed rounded-lg">
            <Server className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Sin OLTs configuradas</p>
            <p className="text-xs">Agrega OLTs para definir los equipos del sitio</p>
          </div>
        ) : (
          <div className="space-y-4">
            {localConfig.olts.map((olt, oltIndex) => (
              <div key={olt.id} className="border rounded-lg overflow-hidden dark:border-dark-600">
                {/* Header OLT */}
                <div 
                  className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 flex items-center justify-between cursor-pointer"
                  onClick={() => toggleOLTExpanded(olt.id)}
                >
                  <div className="flex items-center gap-3">
                    <Server className="w-5 h-5 text-blue-600" />
                    <div>
                      <span className="font-medium">OLT {oltIndex + 1}: {olt.marca} {olt.modelo || '(sin modelo)'}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        {olt.slots.reduce((a, s) => a + s.puertos.length, 0)} puertos
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeOLT(olt.id) }}
                      className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {olt.expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>

                {/* Contenido OLT */}
                {olt.expanded && (
                  <div className="p-4 space-y-4">
                    {/* Info básica OLT */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Marca</label>
                        <select
                          value={olt.marca}
                          onChange={(e) => updateOLT(olt.id, { marca: e.target.value })}
                          className="w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        >
                          {MARCAS_OLT.map(marca => (
                            <option key={marca} value={marca}>{marca}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Modelo</label>
                        <input
                          type="text"
                          value={olt.modelo}
                          onChange={(e) => updateOLT(olt.id, { modelo: e.target.value })}
                          placeholder="MA5800-X7"
                          className="w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Tecnología</label>
                        <select
                          value={olt.tecnologia}
                          onChange={(e) => updateOLT(olt.id, { tecnologia: e.target.value as OLTEquipo['tecnologia'] })}
                          className="w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        >
                          {TECNOLOGIAS.map(tech => (
                            <option key={tech} value={tech}>{tech}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Serial</label>
                        <input
                          type="text"
                          value={olt.serial || ''}
                          onChange={(e) => updateOLT(olt.id, { serial: e.target.value })}
                          placeholder="S/N"
                          className="w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">IP Gestión</label>
                        <input
                          type="text"
                          value={olt.gestionIP || ''}
                          onChange={(e) => updateOLT(olt.id, { gestionIP: e.target.value })}
                          placeholder="192.168.1.1"
                          className={`w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 ${
                            olt.gestionIP && !isValidIP(olt.gestionIP) ? 'border-red-500' : 'dark:border-dark-600'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Puerto Gestión</label>
                        <input
                          type="number"
                          value={olt.gestionPuerto || ''}
                          onChange={(e) => updateOLT(olt.id, { gestionPuerto: parseInt(e.target.value) || undefined })}
                          placeholder="8080"
                          className="w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        />
                      </div>
                    </div>

                    {/* Slots */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium flex items-center gap-1">
                          <Cpu className="w-4 h-4" /> Slots ({olt.slots.length})
                        </span>
                        <button
                          type="button"
                          onClick={() => addSlot(olt.id)}
                          className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          + Slot
                        </button>
                      </div>

                      {olt.slots.length === 0 ? (
                        <div className="text-center py-3 text-gray-400 text-xs border border-dashed rounded">
                          Sin slots configurados
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {olt.slots.map((slot, slotIndex) => (
                            <div key={slotIndex} className="border rounded p-3 dark:border-dark-600 bg-gray-50 dark:bg-dark-800">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">Slot {slot.numero}</span>
                                  <select
                                    value={slot.tipo}
                                    onChange={(e) => updateSlot(olt.id, slotIndex, { tipo: e.target.value as OLTSlot['tipo'] })}
                                    className="text-xs px-2 py-1 border rounded dark:bg-dark-700 dark:border-dark-600"
                                  >
                                    <option value="PON">Tarjeta PON</option>
                                    <option value="UPLINK">Uplink</option>
                                    <option value="CONTROL">Control</option>
                                    <option value="VACIO">Vacío</option>
                                  </select>
                                  {slot.tipo === 'PON' && (
                                    <input
                                      type="text"
                                      value={slot.tarjeta || ''}
                                      onChange={(e) => updateSlot(olt.id, slotIndex, { tarjeta: e.target.value })}
                                      placeholder="Modelo tarjeta"
                                      className="text-xs px-2 py-1 border rounded dark:bg-dark-700 dark:border-dark-600 w-32"
                                    />
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeSlot(olt.id, slotIndex)}
                                  className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>

                              {slot.tipo === 'PON' && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                      <Cable className="w-3 h-3" /> Puertos PON ({slot.puertos.length})
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => addPuertoToSlot(olt.id, slotIndex)}
                                      className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                                    >
                                      + Puerto
                                    </button>
                                  </div>

                                  {slot.puertos.length > 0 && (
                                    <div className="grid grid-cols-4 gap-2">
                                      {slot.puertos.map((puerto, puertoIndex) => (
                                          <div
                                            key={puertoIndex}
                                            className={`relative p-2 border rounded text-center text-xs ${
                                              puerto.estado === 'up'
                                                ? 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700'
                                                : puerto.estado === 'disabled'
                                                ? 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600'
                                                : 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700'
                                            }`}
                                          >
                                            <button
                                              type="button"
                                              onClick={() => removePuertoFromSlot(olt.id, slotIndex, puertoIndex)}
                                              className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full text-[8px] leading-none"
                                            >
                                              ×
                                            </button>
                                            <div className="font-medium">P{puerto.numero}</div>
                                            <select
                                              value={puerto.estado}
                                              onChange={(e) => updatePuerto(olt.id, slotIndex, puertoIndex, { estado: e.target.value as OLTPuerto['estado'] })}
                                              className="w-full mt-1 text-[10px] bg-transparent border-0 p-0 text-center"
                                            >
                                              <option value="up">🟢 UP</option>
                                              <option value="down">🔴 DOWN</option>
                                              <option value="disabled">⚫ OFF</option>
                                            </select>
                                            
                                            {/* Conexión a cable */}
                                            {puerto.conexionCable ? (
                                              <div 
                                                className="mt-1 flex items-center justify-center gap-1 cursor-pointer hover:bg-white/50 rounded p-0.5"
                                                onClick={() => setEditingPuerto({ oltId: olt.id, slotIdx: slotIndex, puertoIdx: puertoIndex })}
                                                title={`${puerto.conexionCable.cableNombre} - ${puerto.conexionCable.bufferColor ? `B${puerto.conexionCable.bufferNum}` : ''} H${puerto.conexionCable.fibraNum}`}
                                              >
                                                <Circle
                                                  className="w-2 h-2"
                                                  fill={getColorHex(puerto.conexionCable.fibraColor)}
                                                  stroke={getColorHex(puerto.conexionCable.fibraColor)}
                                                />
                                                <span className="text-[9px] truncate max-w-[40px]">
                                                  H{puerto.conexionCable.fibraNum}
                                                </span>
                                              </div>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => setEditingPuerto({ oltId: olt.id, slotIdx: slotIndex, puertoIdx: puertoIndex })}
                                                className="mt-1 text-[9px] text-blue-600 hover:underline"
                                              >
                                                + Cable
                                              </button>
                                            )}
                                          </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Modal de conexión de puerto */}
                                  {editingPuerto?.oltId === olt.id && editingPuerto?.slotIdx === slotIndex && (
                                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium">
                                          Conectar Puerto {slot.puertos[editingPuerto.puertoIdx]?.numero} a Cable
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => setEditingPuerto(null)}
                                          className="text-gray-500 hover:text-gray-700"
                                        >
                                          ×
                                        </button>
                                      </div>
                                      
                                      {cablesConectados.length === 0 ? (
                                        <p className="text-xs text-gray-500">No hay cables conectados a este nodo</p>
                                      ) : (
                                        <div className="space-y-2">
                                          {cablesConectados.map(cable => {
                                            const fibras = getFibrasDisponibles(cable)
                                            return (
                                              <div key={cable.id} className="bg-white dark:bg-dark-700 rounded p-2">
                                                <div className="text-xs font-medium mb-1 leading-tight">
                                                  {cableLabelForSelect(cable)}
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                  {fibras.slice(0, 24).map(fibra => {
                                                    const disponible = fibra.estado === 'libre'
                                                    const puerto = slot.puertos[editingPuerto.puertoIdx]
                                                    const seleccionada = puerto?.conexionCable?.fibraId === fibra.fibraId

                                                    return (
                                                      <button
                                                        key={fibra.fibraId}
                                                        type="button"
                                                        onClick={() => disponible && conectarPuertoACable(
                                                          olt.id,
                                                          slotIndex,
                                                          editingPuerto.puertoIdx,
                                                          {
                                                            cableId: cable.id,
                                                            cableNombre: cableLabelShort(cable),
                                                            bufferNum: fibra.bufferNum,
                                                            bufferColor: fibra.bufferColor,
                                                            fibraNum: fibra.fibraNum,
                                                            fibraColor: fibra.fibraColor,
                                                            fibraId: fibra.fibraId
                                                          }
                                                        )}
                                                        disabled={!disponible && !seleccionada}
                                                        className={`p-1 border rounded text-[9px] ${
                                                          seleccionada
                                                            ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/40'
                                                            : disponible
                                                            ? 'hover:border-blue-400 hover:bg-blue-50'
                                                            : 'opacity-40 cursor-not-allowed'
                                                        }`}
                                                        title={`${fibra.bufferNum ? `B${fibra.bufferNum}-` : ''}H${fibra.fibraNum} (${fibra.fibraColor})`}
                                                      >
                                                        <Circle
                                                          className="w-2 h-2"
                                                          fill={getColorHex(fibra.fibraColor)}
                                                          stroke={getColorHex(fibra.fibraColor)}
                                                        />
                                                      </button>
                                                    )
                                                  })}
                                                </div>
                                              </div>
                                            )
                                          })}
                                          
                                          {slot.puertos[editingPuerto.puertoIdx]?.conexionCable && (
                                            <button
                                              type="button"
                                              onClick={() => conectarPuertoACable(olt.id, slotIndex, editingPuerto.puertoIdx, undefined)}
                                              className="text-xs text-red-600 hover:underline"
                                            >
                                              Desconectar
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resumen */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
        <h4 className="font-medium mb-2">Resumen del Sitio</h4>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">OLTs:</span>
            <span className="ml-2 font-medium">{totalOLTs}</span>
          </div>
          <div>
            <span className="text-gray-500">Puertos PON:</span>
            <span className="ml-2 font-medium">{totalPuertos}</span>
          </div>
          <div>
            <span className="text-gray-500">Conectados:</span>
            <span className="ml-2 font-medium text-green-600">{puertosConectados}</span>
          </div>
          <div>
            <span className="text-gray-500">Capacidad:</span>
            <span className="ml-2 font-medium">{totalPuertos * 128} ONTs</span>
          </div>
        </div>
      </div>
    </div>
  )
}
